import { Component, OnDestroy, OnInit, effect, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService, EnumValues, EnumObjectExtended } from '@card-software/library';
import { Invoice, InvoiceItem, S3Object } from '../../../models/invoice/invoice';
import { Shipment } from '../../../models/shipment/shipment';
import { PurchaseOrder, PurchaseOrderItem } from '../../../models/purchase-order/purchase-order';
import { InvoiceProxyService } from '../../../proxies/order/invoice.proxy.service';
import { ShipmentProxyService } from '../../../proxies/order/shipment.proxy.service';
import { InventoryLotProxyService } from '../../../proxies/inventory/inventory-lot.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { quantityValidator } from '../../../validators/invoice/item.validator';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import _ from 'lodash';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ItemMaster } from '../../../models/item/item-master/item-master';
import { UploadFilesService } from '../../../services/upload-files.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { lastValueFrom, Subscription, take } from 'rxjs';
import { FabricProxyService } from '../../../proxies/item/fabric.proxy.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Business } from '../../../models/finance/business';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { BusinessSearchCommand } from '../../../models/business-search';
import { BusinessEntityType } from '../../../models/business-type-entities';
import { IMEXAdminGroup, IMEXViewerGroup } from '../../../models/roles/roles-group';
import { ExchangeRateProxyService } from '../../../proxies/finance/exchange-rate.proxy.service';
import { Currency } from '../../../models/purchase-order/currency';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { PurchaseOrderProxyService } from '../../../proxies/order/purchase-order.proxy.service';
import { TranslateService } from '@ngx-translate/core';
import { InvoiceStatus } from '../../../models/invoice/invoice-status-enum';
import { AuthService } from '@card-software/nexus-utilities';

// Invoice Status Enum (matching CustomerOrderStatus from backend)

@Component({ selector: 'app-invoice-detail', templateUrl: './invoice-detail.component.html', styleUrl: './invoice-detail.component.scss' })
export class InvoiceDetailComponent implements OnInit, OnDestroy {
  uoms: EnumValues = {};
  purchaseOrdersToDisplay: PurchaseOrder[] = [];
  providersToDisplay: Business[] = [];
  itemCodes: ItemMaster[] = [];
  public invoice!: Invoice;
  public isLoading = 0;
  public isNewInvoice = true;
  public purchase!: PurchaseOrder;
  public purchases: PurchaseOrder[] = [];
  public providers: Business[] = [];
  public shipment!: Shipment;
  public shipments: Shipment[] = [];
  public listItemsOriginal: PurchaseOrderItem[] = [];
  public totalAmount = 0;
  public totalQuantitiesKgs = 0;
  public invalidData = false;
  todayExchangeRate = 0;
  poWithoutManufacturer = false;
  currentFiles = signal<File[]>([]);

  listPurchaseOrderSelected = signal<PurchaseOrder[]>([]);

  private static readonly UOM_KG = 0;
  private static readonly UOM_LB = 3;

  // Status transition map: defines which statuses can transition to which
  private readonly statusTransitions: Map<InvoiceStatus, InvoiceStatus[]> = new Map([
    [InvoiceStatus.Draft, [InvoiceStatus.Submitted]],
    [InvoiceStatus.Submitted, [InvoiceStatus.Approved, InvoiceStatus.Rejected]],
    [InvoiceStatus.Approved, []], // No transitions from Approved
    [InvoiceStatus.Rejected, []], // No transitions from Rejected
    [InvoiceStatus.Cancelled, []], // No transitions from Cancelled
  ]);

  status: FormControl<number> = new FormControl(0, {
    nonNullable: true,
    validators: Validators.required,
  });
  // HANDLE FORMS
  public formArrayItems: FormArray<FormGroup> = new FormArray<FormGroup>([]);
  public inputPurchaseOrder: FormControl<PurchaseOrder | null> = new FormControl(null);
  public inputMaker: FormControl<Business | null> = new FormControl({ value: null, disabled: true });
  public inputInvoiceNumber = new FormControl('');
  providerInvoiceNumber = new FormControl('');
  public inputShipment = new FormControl('');
  public inputDueDate = new FormControl({ value: '', disabled: true });
  public inputExchangeRate = new FormControl({ value: 1, disabled: true });
  public inputIssuedDate = new FormControl<Date | null>({ value: null, disabled: true }, [Validators.required]);
  public totalAmountForm = new FormControl(0);
  public differentExchangeRate = new FormControl(false);
  public manufacturerAllItems = new FormControl(false);
  enumValues: EnumValues = {};
  statusEnumObject?: EnumObjectExtended;
  public invalidQuantities = false;
  public blockSendData = false;
  private isManualManufacturerControl = false;
  referenceOriginalProvider = '';
  public itemId = new FormControl('');
  public itemCode = new FormControl('');
  public itemUom = new FormControl('');
  public itemQuantity = new FormControl('');
  public itemUnitPrice = new FormControl('');
  public itemDiscount = new FormControl('');
  public itemTotalPrice = new FormControl('');
  public itemSelected = new FormControl('');
  public totalAmount1Form = new FormControl({ value: 0, disabled: true });

  public totalQuantitiesLbsForm = new FormControl({ value: 0, disabled: true });
  public totalQuantitiesKgsForm = new FormControl({ value: 0, disabled: true });

  public invoiceForm = new FormGroup({
    totalAmount1: this.totalAmount1Form,
    purchaseOrder: this.inputPurchaseOrder,
    providerInvoiceNumber: this.providerInvoiceNumber,
    items: this.formArrayItems,
    totalAmount: this.totalAmountForm,
    dueDate: this.inputDueDate,
    issuedDate: this.inputIssuedDate,
    exchangeRate: this.inputExchangeRate,
    totalQuantitiesLbs: this.totalQuantitiesLbsForm,
    totalQuantitiesKgs: this.totalQuantitiesKgsForm,
    differentExchangeRate: this.differentExchangeRate,
    manufacturerAllItems: this.manufacturerAllItems,
  });
  private subscription: Subscription = new Subscription();
  private subscriptionFormArray: Subscription = new Subscription();
  private _invoiceProxyService = inject(InvoiceProxyService);
  private _purchaseOrderProxyService = inject(PurchaseOrderProxyService);
  private _shipmentProxyService = inject(ShipmentProxyService);
  private inventoryLotProxyService = inject(InventoryLotProxyService);
  private businessProxyService = inject(BusinessProxyService);
  private itemProxyService = inject(ItemMasterProxyService);
  private exchangeRateProxyService = inject(ExchangeRateProxyService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private enumService = inject(EnumService);
  private uploadService = inject(UploadFilesService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private fabricProxyService = inject(FabricProxyService);
  public localeHelperService = inject(LocaleHelperService);
  private translateService = inject(TranslateService);
  private breadcrumbService = inject(BreadcrumbService);

  constructor() {
    effect(() => {
      this.uoms = this.enumService.getEnum('UnitOfMeasure', 'itemEnums')?.values;
      this.statusEnumObject = this.enumService.getEnum('CustomerOrderStatus', 'orderEnums');
      this.enumValues = this.statusEnumObject?.values ?? {};
    });
  }

  public get invoicePurchaseOrderStatusOptions(): EnumValues {
    if (!this.enumValues || typeof this.enumValues !== 'object') return {};

    const currentStatus = this.invoice?.status ?? InvoiceStatus.Draft;

    // No changes allowed from Approved, Rejected, or Cancelled
    if (currentStatus === InvoiceStatus.Approved || currentStatus === InvoiceStatus.Rejected || currentStatus === InvoiceStatus.Cancelled) {
      return {};
    }

    // Get allowed next statuses based on current status
    const allowedNextStatuses = this.statusTransitions.get(currentStatus) ?? [];

    // Build the filtered options
    const filtered: EnumValues = {};

    // Always include current status so it shows as selected
    if (this.enumValues[currentStatus]) {
      filtered[currentStatus] = this.enumValues[currentStatus];
    }

    // Add allowed next statuses
    for (const status of allowedNextStatuses) {
      if (this.enumValues[status]) {
        filtered[status] = this.enumValues[status];
      }
    }

    // Add Cancelled option for Admin (except when already Approved)
    if (AuthService.hasRole(IMEXAdminGroup.InvoiceAdmin) && currentStatus !== InvoiceStatus.Approved) {
      if (this.enumValues[InvoiceStatus.Cancelled]) {
        filtered[InvoiceStatus.Cancelled] = this.enumValues[InvoiceStatus.Cancelled];
      }
    }

    // Check if user has permission to change status
    if (!this.canEdit()) {
      return {};
    }

    return filtered;
  }

  canEdit() {
    // if Admin
    if (AuthService.hasRole(IMEXAdminGroup.InvoiceAdmin)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(IMEXViewerGroup.InvoiceViewer)) {
      return false;
    }
    // Default
    return false;
  }

  /** Columns in the items table, so the footer rows span the same width as the header. */
  get itemsTableColumnCount(): number {
    const hasManufacturer = (this.listPurchaseOrderSelected()[0]?.type ?? 0) > 0;
    return 5 + (hasManufacturer ? 1 : 0) + (this.canEdit() ? 1 : 0);
  }

  ngOnInit(): void {
    this.formArrayItems.controls.forEach((group) => {
      group.valueChanges.subscribe(() => {
        const quantity = group.get('quantity')?.value || 0;
        const unitPrice = group.get('unitPrice')?.value || 0;
        group.get('totalPrice')?.setValue(quantity * unitPrice, { emitEvent: false });
      });
    });

    this.invoiceForm.controls.purchaseOrder.valueChanges.subscribe((data) => {
      this.resetManualControl();
      if (_.isNil(data?.id)) {
        return;
      }
      // Load item codes from all selected purchase orders when a new one is added
      this.loadItemCodesFromSelectedPurchaseOrders();
    });
    this.subscription.add(
      this.invoiceForm.controls.issuedDate.valueChanges.subscribe((value) => {
        const selectedPurchaseOrders = this.listPurchaseOrderSelected();
        const purchaseOrder = selectedPurchaseOrders.length > 0 ? selectedPurchaseOrders[0] : null;

        this.populateDueDate(value!, purchaseOrder?.paymentTermCode ?? '');
        if (this.isNewInvoice) {
          this.exchangeRateProxyService
            .searchExchangeRate({
              date: this.parseDate(this.inputIssuedDate.value ?? new Date()) as string,
              currency: Currency.USD,
            })
            .subscribe({
              next: (response) => {
                if (response.data && response.data.length > 0) {
                  this.inputExchangeRate.setValue(response.data[0].rateToUSD);
                  this.todayExchangeRate = response.data[0].rateToUSD;
                } else {
                  this.inputExchangeRate.setValue(1);
                }
              },
              error: (err) => {
                console.error('Error fetching exchange rate:', err);
                this.inputExchangeRate.setValue(0);
              },
            });
        }
      }),
    );
    this.invoiceForm.get('differentExchangeRate')?.valueChanges.subscribe((value) => {
      if (value) {
        this.inputExchangeRate.enable();
      } else {
        this.inputExchangeRate.disable();
        this.inputExchangeRate.setValue(this.todayExchangeRate);
      }
    });

    new FormGroup({
      quantity: new FormControl(0),
      unitPrice: new FormControl(0),
      totalPrice: new FormControl({ value: 0, disabled: true }),
    });
    this.manufacturerAllItems.valueChanges.subscribe((value) => {
      this.isManualManufacturerControl = true;

      if (value) {
        this.inputMaker.enable();
        this.formArrayItems.controls.forEach((item) => {
          item.get('manufacturerReference')?.disable();
          item.get('manufacturerReference')?.setValue(this.inputMaker.value);
        });
      } else {
        this.inputMaker.disable();
        this.formArrayItems.controls.forEach((item) => {
          item.get('manufacturerReference')?.enable();
        });
      }
    });

    this.inputMaker.valueChanges.subscribe((value) => {
      if (this.manufacturerAllItems.value) {
        this.formArrayItems.controls.forEach((item) => {
          item.get('manufacturerReference')?.setValue(value);
        });
      }
    });
    this.activatedRoute.queryParams.subscribe((params) => {
      const id = params['id'];
      if (!id) this.cancel();
      if (id !== 'new') {
        this.getInvoice(id);
        this.invoiceForm.get('purchaseOrder')?.disable();
        this.inputExchangeRate.disable();
        this.isNewInvoice = false;
        this.differentExchangeRate.disable();
      } else {
        this.breadcrumbService.setCurrent('Common_New');
      }
    });
    this.invoiceForm.valueChanges.subscribe(() => {
      this.getTotalPriceAndQuantity();
    });

    // Status change confirmation handler
    this.status.valueChanges.subscribe((newStatus) => {
      if (!_.isNil(this.invoice) && !_.isEqual(this.invoice?.status, newStatus)) {
        const currentStatusName = this.getInvoiceStatusName(this.invoice!.status);
        const newStatusName = this.getInvoiceStatusName(newStatus);
        const isCancelling = newStatus === InvoiceStatus.Cancelled;

        // For cancellation, check if it's allowed
        if (isCancelling) {
          this.checkCanCancelInvoice().then((canCancel) => {
            if (!canCancel.allowed) {
              GlobalMessageUtil.error('Cannot Cancel Invoice', canCancel.reason || 'Invoice cannot be cancelled');
              this.status.setValue(this.invoice!.status, { emitEvent: false });
              return;
            }

            // Show cancellation confirmation
            this.showStatusChangeConfirmation(currentStatusName, newStatusName, isCancelling, newStatus);
          });
        } else {
          // Show normal status change confirmation
          this.showStatusChangeConfirmation(currentStatusName, newStatusName, isCancelling, newStatus);
        }
      }
    });

    if (!this.canEdit()) {
      this.invoiceForm.disable();
      this.itemSelected.disable();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.subscriptionFormArray.unsubscribe();
  }

  populateDueDate(date: Date | string, paymentTerm: string): void {
    switch (paymentTerm) {
      case 'MD-1': {
        //NET 7 days
        const issuedDate7 = new Date(date ?? '');
        const dueDate7 = new Date(issuedDate7);
        dueDate7.setDate(dueDate7.getDate() + 7);
        this.inputDueDate?.setValue(this.parseDate(dueDate7) as string);
        break;
      }
      case 'MD-2': {
        //NET 15 days
        const issuedDate15 = new Date(date ?? '');
        const dueDate15 = new Date(issuedDate15);
        dueDate15.setDate(dueDate15.getDate() + 15);
        this.inputDueDate?.setValue(this.parseDate(dueDate15) as string);
        break;
      }
      case 'MD-3': {
        //NET 20 days
        const issuedDate20 = new Date(date ?? '');
        const dueDate20 = new Date(issuedDate20);
        dueDate20.setDate(dueDate20.getDate() + 20);
        this.inputDueDate?.setValue(this.parseDate(dueDate20) as string);
        break;
      }
      case 'MD-4': {
        //NET 30 days
        const issuedDate30 = new Date(date ?? '');
        const dueDate30 = new Date(issuedDate30);
        dueDate30.setDate(dueDate30.getDate() + 30);
        this.inputDueDate?.setValue(this.parseDate(dueDate30) as string);
        break;
      }
      case 'MD-5': {
        //NET 45 days
        const issuedDate45 = new Date(date ?? '');
        const dueDate45 = new Date(issuedDate45);
        dueDate45.setDate(dueDate45.getDate() + 45);
        this.inputDueDate?.setValue(this.parseDate(dueDate45) as string);
        break;
      }
      case 'MD-6': {
        //NET 60 days
        const issuedDate60 = new Date(date ?? '');
        const dueDate60 = new Date(issuedDate60);
        dueDate60.setDate(dueDate60.getDate() + 60);
        this.inputDueDate?.setValue(this.parseDate(dueDate60) as string);
        break;
      }
      case 'MD-7': {
        //NET 75 days
        const issuedDate75 = new Date(date ?? '');
        const dueDate75 = new Date(issuedDate75);
        dueDate75.setDate(dueDate75.getDate() + 75);
        this.inputDueDate?.setValue(this.parseDate(dueDate75) as string);
        break;
      }
      case 'MD-8': {
        //NET 90 days
        const issuedDate90 = new Date(date ?? '');
        const dueDate90 = new Date(issuedDate90);
        dueDate90.setDate(dueDate90.getDate() + 90);
        this.inputDueDate?.setValue(this.parseDate(dueDate90) as string);
        break;
      }
      case 'MD-9': {
        //NET 120 days
        const issuedDate120 = new Date(date ?? '');
        const dueDate120 = new Date(issuedDate120);
        dueDate120.setDate(dueDate120.getDate() + 120);
        this.inputDueDate?.setValue(this.parseDate(dueDate120) as string);
        break;
      }
      default:
        this.inputDueDate?.setValue('');
        this.inputDueDate.enable();
        break;
    }
  }

  searchPurchaseOrder(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();
    this._purchaseOrderProxyService
      .searchPurchaseOrder(
        {
          purchaseOrderNumber: query,
        },
        1,
        25,
      )
      .subscribe({
        next: (response) => {
          if (response.data?.items === null) {
            this.purchaseOrdersToDisplay = [];
            return;
          }
          this.purchaseOrdersToDisplay = response.data?.items ?? [];
        },
        error: (e) => {
          console.error(e);
          this.purchaseOrdersToDisplay = [];
        },
      });
  }

  addPurchaseOrderAtList(event: AutoCompleteSelectEvent) {
    const PO = event.value;
    if (!PO) return;
    const matchPO = this.listPurchaseOrderSelected().find((p) => p.id === PO.id);

    const selectedPOs = this.listPurchaseOrderSelected();
    if (selectedPOs.length > 0) {
      const existingType = selectedPOs[0].type;
      if (PO.type !== existingType) {
        GlobalMessageUtil.warn(
          this.translateService.instant('InvoiceDetailPage_Messages.POMustBeSameTypeTitle'),
          this.translateService.instant('InvoiceDetailPage_Messages.POMustBeSameType'),
        );
        this.inputPurchaseOrder.setValue(null, { emitEvent: false });
        return;
      }

      // Validate that all POs have the same currency
      const existingCurrency = selectedPOs[0].currency;
      if (PO.currency !== existingCurrency) {
        GlobalMessageUtil.warn(
          'Currency Mismatch',
          'All purchase orders must have the same currency. Please select a purchase order with the same currency.',
        );
        this.inputPurchaseOrder.setValue(null, { emitEvent: false });
        return;
      }
    }

    if (!matchPO) {
      // Fetch full PO details including manufacturer references for items
      this.isLoading++;
      this._purchaseOrderProxyService.getPurchaseOrderById(PO.id!).subscribe({
        next: (response) => {
          this.isLoading--;
          if (response.data) {
            this.inputIssuedDate.enable({ emitEvent: false });
            const fullPO = response.data;

            // Fetch provider information if needed
            if (fullPO.providerReference && !fullPO.provider) {
              this.businessProxyService.getByIds([fullPO.providerReference]).subscribe({
                next: (providerResponse) => {
                  if (providerResponse.data && providerResponse.data.length > 0) {
                    fullPO.provider = providerResponse.data[0];
                  }
                },
                error: (e) => {
                  console.error('Error fetching provider:', e);
                },
              });
            }

            // Fetch manufacturers if they exist
            const manufacturerIds: string[] = [];
            fullPO.items.forEach((item) => {
              const ref = item.manufacturerReference as Business | string | undefined;
              if (ref) {
                if (typeof ref === 'string') {
                  const refStr = ref as string;
                  if (refStr.trim() !== '' && !manufacturerIds.includes(refStr)) {
                    manufacturerIds.push(refStr);
                  }
                } else if (typeof ref === 'object' && 'id' in ref && ref.id) {
                  if (!manufacturerIds.includes(ref.id)) {
                    manufacturerIds.push(ref.id);
                  }
                }
              }
            });

            if (manufacturerIds.length > 0) {
              this.businessProxyService.getByIds(manufacturerIds).subscribe({
                next: (manufacturersResponse) => {
                  const manufacturers = manufacturersResponse.data ?? [];

                  // Map manufacturers to items
                  fullPO.items.forEach((item) => {
                    const ref = item.manufacturerReference;
                    if (ref) {
                      if (typeof ref === 'string') {
                        const manufacturer = manufacturers.find((m) => m.id === ref);
                        if (manufacturer) {
                          item.manufacturerReference = manufacturer;
                        }
                      } else if (typeof ref === 'object' && 'id' in ref) {
                        // Already a Business object, ensure it has full data
                        const manufacturer = manufacturers.find((m) => m.id === ref.id);
                        if (manufacturer) {
                          item.manufacturerReference = manufacturer;
                        }
                      }
                    }
                  });

                  // Set manufacturers array on the PO for easy access
                  fullPO.manufacturers = manufacturers;

                  this.listPurchaseOrderSelected().push(fullPO);

                  this.loadItemCodesFromSelectedPurchaseOrders();
                },
                error: (e) => {
                  console.error('Error fetching manufacturers:', e);
                  this.listPurchaseOrderSelected().push(fullPO);
                  this.loadItemCodesFromSelectedPurchaseOrders();
                },
              });
            } else {
              this.listPurchaseOrderSelected().push(fullPO);
              this.loadItemCodesFromSelectedPurchaseOrders();
            }
          }
        },
        error: (e) => {
          this.isLoading--;
          console.error('Error fetching purchase order:', e);
        },
      });
    } else {
      GlobalMessageUtil.warn('Warning', 'Purchase Order already selected');
    }
    // Clear the autocomplete input
    this.inputPurchaseOrder.setValue(null, { emitEvent: false });
  }

  deletePurchaseOrderAtList(index: number) {
    const deletedPO = this.listPurchaseOrderSelected()[index];
    this.listPurchaseOrderSelected().splice(index, 1);

    // Remove items that belong to the deleted purchase order
    const itemsToRemove: number[] = [];
    this.formArrayItems.controls.forEach((control, idx) => {
      if (control.get('purchaseOrderReference')?.value === deletedPO.id) {
        itemsToRemove.push(idx);
      }
    });

    // Remove in reverse order to maintain correct indices
    itemsToRemove.reverse().forEach((idx) => {
      this.formArrayItems.removeAt(idx);
    });

    // Reload item codes from remaining purchase orders
    this.loadItemCodesFromSelectedPurchaseOrders();
  }

  searchProvider(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();

    // Get manufacturer references from all selected purchase orders
    const selectedPOs = this.listPurchaseOrderSelected();
    const manufacturersReferenceSet = new Set<string>();

    selectedPOs.forEach((po) => {
      if (po.manufacturersReferences && po.manufacturersReferences.length > 0) {
        po.manufacturersReferences.forEach((ref) => manufacturersReferenceSet.add(ref));
      }
    });

    const manufacturersReference = Array.from(manufacturersReferenceSet);

    if (manufacturersReference.length <= 0) {
      this.providersToDisplay = [];
      this.poWithoutManufacturer = true;
      return;
    }
    this.poWithoutManufacturer = false;
    this.businessProxyService.getByIds(manufacturersReference).subscribe({
      next: (response) => {
        // Filter providers by query string (case-insensitive)
        this.providersToDisplay = (response.data ?? []).filter((provider) => provider.name?.toLowerCase().includes(query));
        if (this.providersToDisplay.length > 8) {
          this.providersToDisplay = this.providersToDisplay.slice(0, 8);
        }
      },
      error: (e) => {
        console.error(e);
        this.providersToDisplay = [];
      },
    });
  }

  getManufacturersForRow(rowIndex: number): Business[] {
    const formGroup = this.formArrayItems.at(rowIndex);
    const selectedPO = formGroup.get('selectedPurchaseOrder')?.value as PurchaseOrder;

    if (!selectedPO || !selectedPO.manufacturers || selectedPO.manufacturers.length === 0) {
      return [];
    }

    return selectedPO.manufacturers;
  }

  searchProviderForRow(event: AutoCompleteCompleteEvent, rowIndex: number) {
    const query = event.query.toLowerCase();
    const manufacturers = this.getManufacturersForRow(rowIndex);

    if (manufacturers.length === 0) {
      this.providersToDisplay = [];
      return;
    }

    // Filter manufacturers by query
    this.providersToDisplay = manufacturers.filter((provider) => provider.name?.toLowerCase().includes(query));

    if (this.providersToDisplay.length > 8) {
      this.providersToDisplay = this.providersToDisplay.slice(0, 8);
    }
  }

  loadItemCodesFromSelectedPurchaseOrders() {
    const selectedPOs = this.listPurchaseOrderSelected();
    if (selectedPOs.length === 0) {
      this.itemCodes = [];
      return;
    }

    this.isLoading++;
    const allItemCodes = selectedPOs.flatMap((po) => po.items.map((item) => item.itemCode));
    const uniqueItemCodes = [...new Set(allItemCodes)];
    const itemMasterCodeBatchCommand = { itemCodes: uniqueItemCodes };

    this.itemProxyService.getItemCodesBatch(itemMasterCodeBatchCommand).subscribe({
      next: (response) => {
        this.isLoading--;
        this.itemCodes = response.data ?? [];
      },
      error: (e) => {
        console.error(e);
        this.isLoading--;
      },
    });
  }

  getAvailablePurchaseOrdersForItem(itemCode: string): PurchaseOrder[] {
    return this.listPurchaseOrderSelected().filter((po) => po.items.some((item) => item.itemCode === itemCode));
  }

  get purchaseOrderItems() {
    const selectedPOs = this.listPurchaseOrderSelected();
    if (selectedPOs.length === 0) return [];

    // Get all unique item codes from all selected purchase orders
    const itemMap = new Map<string, PurchaseOrderItem & { item: ItemMaster }>();

    selectedPOs.forEach((po) => {
      po.items.forEach((item) => {
        if (!itemMap.has(item.itemCode)) {
          itemMap.set(item.itemCode, {
            ...item,
            item: this.getItemMasterForPurchaseOrderItemId(item.itemCode) ?? ({} as ItemMaster),
          });
        }
      });
    });

    return Array.from(itemMap.values());
  }

  private getInvoice(id: string): void {
    if (!id) return;

    this.isLoading++;
    this._invoiceProxyService.getInvoice(id).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.data) {
          this.invoice = response.data;
          this.breadcrumbService.setCurrent(this.invoice.invoiceNumber);
          this.status.setValue(this.invoice.status);
          this.referenceOriginalProvider = this.invoice.providerReference ?? '';

          // Load purchase orders and items
          this.loadExistingInvoiceData(this.invoice);
        }
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  getItemMasterForPurchaseOrderItemId(id: string): ItemMaster | undefined {
    const itemMaster = this.itemCodes.find((x) => x.itemCode === id);
    if (itemMaster) {
      itemMaster.fieldToDisplay = '(' + itemMaster.itemCode + ') ' + this.localeHelperService.getLocalizedValue(itemMaster.itemDescription);
      return itemMaster;
    }
    return undefined;
  }

  getExistingPurchase(items: InvoiceItem[], purchaseOrderId: string) {
    this.isLoading++;
    this._purchaseOrderProxyService.getPurchaseOrderById(purchaseOrderId).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.data) {
          this.purchase = response.data;
          this.invoiceForm.controls.purchaseOrder.setValue(this.purchase);
          this.invoiceForm.controls.issuedDate.setValue(this.invoice.invoiceIssuedDate ? new Date(this.invoice.invoiceIssuedDate) : null, {
            emitEvent: false,
          });
          this.populateDueDate(this.invoice.invoiceIssuedDate ?? new Date(), this.purchase.paymentTermCode ?? '');
        }

        const manufacturerIds = items
          .map((item) => item.manufacturerReference)
          .filter((id): id is string => typeof id === 'string' && id.trim() !== '')
          .filter((id, index, array) => array.indexOf(id) === index);
        if (manufacturerIds.length > 0) {
          this.businessProxyService.getByIds(manufacturerIds).subscribe({
            next: (manufacturersResponse) => {
              const manufacturers = manufacturersResponse.data ?? [];

              items.forEach((item) => {
                const itemData = this.purchase.items.find((x) => x.id === item.itemInPurchaseOrderId);
                if (itemData) {
                  this.listItemsOriginal.push(itemData);

                  const manufacturer = manufacturers.find((m) => m.id === item.manufacturerReference);

                  this._pushItemArrayForm(this.setDataIntoItemForm(itemData, item.quantity, manufacturer));
                }
              });

              setTimeout(() => {
                this.checkIfAllManufacturersAreSame();
              }, 0);
            },
            error: (e) => {
              console.error('Error fetching manufacturers:', e);
              this.processItemsWithoutManufacturers(items);
            },
          });
        } else {
          this.processItemsWithoutManufacturers(items);
        }
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  getUnitName(uomKey: number | null): string {
    if (uomKey === null || uomKey === undefined) return '';
    return this.uoms[uomKey] || '';
  }

  getItemMaster(itemCode: string): ItemMaster | undefined {
    return this.itemCodes.find((x) => x.itemCode === itemCode);
  }

  get totalAmount1(): number {
    return this.formArrayItems.controls.reduce((acc, item) => {
      return item.get('quantity')?.value * item.get('unitPrice')?.value + acc;
    }, 0);
  }

  getShipment(event: Event) {
    const id = (event.target as HTMLSelectElement).value;
    if (id === '' || id === undefined) return;
    this._shipmentProxyService.getShipment(id).subscribe({ error: (e) => console.error(e) });
  }

  private _getShipments() {
    this.isLoading++;
    this._shipmentProxyService.getShipments().subscribe({
      next: (response) => {
        this.shipments = response.data ?? [];
        this.isLoading--;
      },
      error: (e) => {
        console.error(e);
        this.isLoading--;
      },
    });
  }

  private _getProviders() {
    this.isLoading++;
    // Fetch providers from the provider proxy service
    // Assuming the provider proxy service has a method to search or get all providers
    const searchCommand: BusinessSearchCommand = {
      name: '',
      code: '',
      businessTypes: [BusinessEntityType.Provider],
    };

    this.businessProxyService.search(searchCommand).subscribe({
      next: (response) => {
        this.providers = response.data?.items ?? [];
        this.isLoading--;
      },
      error: (e) => {
        console.error(e);
        this.isLoading--;
      },
    });
  }

  private loadExistingInvoiceData(invoice: Invoice) {
    if (!invoice) return;

    // Set basic invoice data

    // Clear existing POs to avoid duplicates
    this.listPurchaseOrderSelected.set(invoice.purchaseOrders ?? []);
    this.invoiceForm.controls.exchangeRate.setValue(invoice.exchangeRateToUSD ?? 0);
    this.invoiceForm.controls.issuedDate.setValue(invoice.invoiceIssuedDate ? new Date(invoice.invoiceIssuedDate) : null);
    this.invoiceForm.controls.dueDate.setValue(this.parseDate(invoice.dueDate ?? '') as string);
    this.totalAmountForm.setValue(invoice.totalAmount);
    this.providerInvoiceNumber.setValue(invoice.providerInvoiceNumber ?? '');
    this.inputMaker.setValue(invoice.provider ?? null);

    // Load purchase orders
    const poReferences = invoice.purchaseOrderReferences ?? [];
    if (poReferences.length === 0) {
      return;
    }

    this.isLoading++;
    // Fetch all purchase orders
    const poPromises = poReferences.map((poRef) => lastValueFrom(this._purchaseOrderProxyService.getPurchaseOrderById(poRef)));

    Promise.all(poPromises)
      .then((responses) => {
        this.isLoading--;

        // Process each PO
        responses.forEach((response) => {
          if (response.data) {
            const fullPO = response.data;

            // Check if PO already exists in the list (avoid duplicates)
            const existingPO = this.listPurchaseOrderSelected().find((po) => po.id === fullPO.id);

            // Fetch provider if needed
            if (fullPO.providerReference && !fullPO.provider) {
              this.businessProxyService.getByIds([fullPO.providerReference]).subscribe({
                next: (providerResponse) => {
                  if (providerResponse.data && providerResponse.data.length > 0) {
                    fullPO.provider = providerResponse.data[0];
                  }
                },
              });
            }

            // Fetch manufacturers for items
            const manufacturerIds: string[] = [];
            fullPO.items.forEach((item) => {
              const ref = item.manufacturerReference as Business | string | undefined;
              if (ref) {
                if (typeof ref === 'string') {
                  const refStr = ref as string;
                  if (refStr.trim() !== '' && !manufacturerIds.includes(refStr)) {
                    manufacturerIds.push(refStr);
                  }
                } else if (typeof ref === 'object' && 'id' in ref && ref.id) {
                  if (!manufacturerIds.includes(ref.id)) {
                    manufacturerIds.push(ref.id);
                  }
                }
              }
            });

            if (manufacturerIds.length > 0) {
              this.businessProxyService.getByIds(manufacturerIds).subscribe({
                next: (manufacturersResponse) => {
                  const manufacturers = manufacturersResponse.data ?? [];

                  fullPO.items.forEach((item) => {
                    const ref = item.manufacturerReference;
                    if (ref) {
                      if (typeof ref === 'string') {
                        const manufacturer = manufacturers.find((m) => m.id === ref);
                        if (manufacturer) {
                          item.manufacturerReference = manufacturer;
                        }
                      } else if (typeof ref === 'object' && 'id' in ref) {
                        const manufacturer = manufacturers.find((m) => m.id === ref.id);
                        if (manufacturer) {
                          item.manufacturerReference = manufacturer;
                        }
                      }
                    }
                  });

                  fullPO.manufacturers = manufacturers;
                },
              });
            }

            // Add PO to list
            if (existingPO) {
              //remove from signal and add fullPO
              const currentPOs = this.listPurchaseOrderSelected();
              const index = currentPOs.findIndex((po) => po.id === existingPO.id);
              if (index !== -1) {
                currentPOs.splice(index, 1, fullPO);
                this.listPurchaseOrderSelected.set(currentPOs);
              }
            } else {
              this.listPurchaseOrderSelected().push(fullPO);
            }
          }
        });

        // Load item codes from all POs
        this.loadItemCodesFromSelectedPurchaseOrders();

        // Load invoice items after a delay to ensure POs and item codes are loaded
        setTimeout(() => {
          this.loadInvoiceItems(invoice);
        }, 500);
      })
      .catch((error) => {
        this.isLoading--;
        console.error('Error loading purchase orders:', error);
      });
  }

  private loadInvoiceItems(invoice: Invoice) {
    if (!invoice.items || invoice.items.length === 0) return;

    // Clear existing items to avoid duplicates
    this.formArrayItems.clear();

    const selectedPOs = this.listPurchaseOrderSelected();

    // Collect all manufacturer IDs that need to be fetched
    const manufacturerIdsToFetch: string[] = [];
    const manufacturerMap = new Map<string, Business>();

    invoice.items.forEach((invoiceItem) => {
      if (invoiceItem.manufacturerReference) {
        const po = selectedPOs.find((p) => p.id === invoiceItem.purchaseOrderReference);
        if (po) {
          const poItem = po.items.find((item) => item.id === invoiceItem.itemInPurchaseOrderId);
          if (poItem && poItem.manufacturerReference) {
            // Check if manufacturer is already a Business object
            if (typeof poItem.manufacturerReference === 'object' && 'id' in poItem.manufacturerReference) {
              manufacturerMap.set(invoiceItem.manufacturerReference, poItem.manufacturerReference);
            } else if (!manufacturerIdsToFetch.includes(invoiceItem.manufacturerReference)) {
              manufacturerIdsToFetch.push(invoiceItem.manufacturerReference);
            }
          } else if (!manufacturerIdsToFetch.includes(invoiceItem.manufacturerReference)) {
            manufacturerIdsToFetch.push(invoiceItem.manufacturerReference);
          }
        }
      }
    });

    // Fetch all manufacturers at once if needed
    if (manufacturerIdsToFetch.length > 0) {
      this.businessProxyService.getByIds(manufacturerIdsToFetch).subscribe({
        next: (response) => {
          const manufacturers = response.data ?? [];
          manufacturers.forEach((m) => {
            if (m.id) manufacturerMap.set(m.id, m);
          });

          // Now create form groups with all manufacturers loaded
          this.createInvoiceItemForms(invoice, selectedPOs, manufacturerMap);
        },
        error: (e) => {
          console.error('Error fetching manufacturers:', e);
          // Create forms without manufacturers if fetch fails
          this.createInvoiceItemForms(invoice, selectedPOs, manufacturerMap);
        },
      });
    } else {
      // No manufacturers to fetch, create forms directly
      this.createInvoiceItemForms(invoice, selectedPOs, manufacturerMap);
    }
  }

  private createInvoiceItemForms(invoice: Invoice, selectedPOs: PurchaseOrder[], manufacturerMap: Map<string, Business>) {
    invoice.items.forEach((invoiceItem) => {
      // Find the PO for this item
      const po = selectedPOs.find((p) => p.id === invoiceItem.purchaseOrderReference);
      if (!po) return;

      // Find the item in the PO
      const poItem = po.items.find((item) => item.id === invoiceItem.itemInPurchaseOrderId);
      if (!poItem) return;

      // Get manufacturer from map
      const manufacturer = invoiceItem.manufacturerReference ? manufacturerMap.get(invoiceItem.manufacturerReference) : undefined;

      // Create form group
      const formGroup = this.setDataIntoItemForm(poItem, invoiceItem.quantity, manufacturer, invoiceItem.purchaseOrderReference);

      // Set the selected purchase order
      formGroup.get('selectedPurchaseOrder')?.setValue(po);

      // Add to form array
      this._pushItemArrayForm(formGroup);
    });

    // Check if all manufacturers are the same
    setTimeout(() => {
      this.checkIfAllManufacturersAreSame();
    }, 0);
  }

  public setDataInputs(invoice: Invoice) {
    if (invoice) {
      // this.invoiceForm.controls.dueDate.setValue((this.parseDate(invoice.dueDate ?? '') as string) ?? '');
      // this.invoiceForm.controls.issuedDate.setValue((this.parseDate(invoice.invoiceIssuedDate ?? '') as string) ?? '');
      this.invoiceForm.controls.exchangeRate.setValue(invoice.exchangeRateToUSD ?? 0);
      this.inputMaker.setValue(invoice.provider ?? null);
      this.totalAmountForm.setValue(invoice.totalAmount);
      this.providerInvoiceNumber.setValue(invoice.providerInvoiceNumber ?? '');
    }
  }

  async getItemData(itemCode: string) {
    await lastValueFrom(this.fabricProxyService.getFabricByItemCode(itemCode).pipe(take(1)));
  }

  setDataIntoItemForm(data: PurchaseOrderItem, quantity?: number, manufacturer?: Business, purchaseOrderRef?: string): FormGroup {
    return new FormGroup({
      id: new FormControl(data.id),
      itemCode: new FormControl(data.itemCode),
      purchaseOrderReference: new FormControl(purchaseOrderRef || data.purchaseOrderReference),
      itemDescription: new FormControl(''),
      uom: new FormControl(data.uom),
      quantity: new FormControl(quantity ? quantity : (data.quantity ?? 0), [Validators.min(1), quantityValidator(data.quantity)]),
      unitPrice: new FormControl({ value: data.unitPrice, disabled: true }),
      discount: new FormControl({ value: data.discount, disabled: true }),
      totalPrice: new FormControl({ value: data.totalPrice, disabled: true }),
      manufacturerReference: new FormControl(manufacturer || null, [Validators.required]),
      selectedPurchaseOrder: new FormControl(null, [Validators.required]),
    });
  }

  private _pushItemArrayForm(formGroup: FormGroup) {
    if (!this.canEdit()) {
      formGroup.disable();
    }
    this.formArrayItems.push(formGroup);
    this.subscriptionFormArray.add(
      formGroup.get('manufacturerReference')?.valueChanges.subscribe(() => {
        if (!this.manufacturerAllItems.value) {
          setTimeout(() => {
            this.checkIfAllManufacturersAreSame();
          }, 0);
        }
      }),
    );

    // Listen to purchase order selection changes
    this.subscriptionFormArray.add(
      formGroup.get('selectedPurchaseOrder')?.valueChanges.subscribe((selectedPO: PurchaseOrder) => {
        if (selectedPO) {
          const itemCode = formGroup.get('itemCode')?.value;

          // Check if this combination already exists in another row
          const currentIndex = this.formArrayItems.controls.indexOf(formGroup);
          const isDuplicate = this.formArrayItems.controls.some((control, index) => {
            if (index === currentIndex) return false; // Skip current row
            return control.get('itemCode')?.value === itemCode && control.get('purchaseOrderReference')?.value === selectedPO.id;
          });

          if (isDuplicate) {
            GlobalMessageUtil.warn('Warning', 'This item with the selected purchase order already exists in the table');
            formGroup.get('selectedPurchaseOrder')?.setValue(null, { emitEvent: false });
            return;
          }

          formGroup.get('purchaseOrderReference')?.setValue(selectedPO.id);

          // Update item details from the selected PO
          const selectedItem = selectedPO.items.find((item) => item.itemCode === itemCode);
          if (selectedItem) {
            formGroup.get('id')?.setValue(selectedItem.id);
            formGroup.get('uom')?.setValue(selectedItem.uom);
            formGroup.get('quantity')?.setValue(selectedItem.quantity);
            formGroup.get('unitPrice')?.setValue(selectedItem.unitPrice);
            formGroup.get('discount')?.setValue(selectedItem.discount);
            formGroup.get('totalPrice')?.setValue(selectedItem.totalPrice);

            // Update quantity validator with the new item's max quantity
            const quantityControl = formGroup.get('quantity');
            quantityControl?.setValidators([Validators.min(1), quantityValidator(selectedItem.quantity)]);
            quantityControl?.updateValueAndValidity();

            // Set manufacturer from the selected item in PO
            if (selectedItem.manufacturerReference && !this.manufacturerAllItems.value) {
              // Get the manufacturer business object
              if (selectedItem.manufacturerReference instanceof Object && 'id' in selectedItem.manufacturerReference) {
                // If it's already a Business object
                formGroup.get('manufacturerReference')?.setValue(selectedItem.manufacturerReference);
              } else {
                // If it's just an ID, fetch the manufacturer
                const manufacturerId = selectedItem.manufacturerReference;
                this.businessProxyService.getByIds([manufacturerId as string]).subscribe({
                  next: (response) => {
                    if (response.data && response.data.length > 0) {
                      formGroup.get('manufacturerReference')?.setValue(response.data[0]);
                    }
                  },
                  error: (e) => {
                    console.error('Error fetching manufacturer:', e);
                  },
                });
              }
            }
          }
        }
      }),
    );
  }

  public _validateQuantityNotExceeded(data: FormGroup) {
    this.invalidQuantities = data.get('quantity')?.hasError('quantityExceeded') ?? false;
  }

  addItemIntoArrayForm(itemCode: string) {
    if (!itemCode) return;

    // Find the selected item code from all purchase orders
    const selectedPOs = this.listPurchaseOrderSelected();

    // Get all POs that have this item code
    const posWithItem = selectedPOs.filter((po) => po.items.some((item) => item.itemCode === itemCode));

    if (posWithItem.length === 0) {
      GlobalMessageUtil.warn('Warning', 'Item not found in selected purchase orders');
      return;
    }

    // Get the first PO's item data as template (we'll let user select PO in the table)
    const firstPO = posWithItem[0];
    const itemData = firstPO.items.find((item) => item.itemCode === itemCode);

    if (!itemData) return;

    // Create form without pre-selecting a PO - user will select it in the table
    const formGroup = this.setDataIntoItemForm(
      itemData,
      undefined,
      this.manufacturerAllItems.value ? this.inputMaker.value! : undefined,
      undefined, // No PO reference yet
    );

    if (this.manufacturerAllItems.value) {
      //disable manufacturer field
      formGroup.get('manufacturerReference')?.disable();
    }

    // Don't set selectedPurchaseOrder yet - user will select it in the table
    formGroup.get('selectedPurchaseOrder')?.setValue(null);

    this._pushItemArrayForm(formGroup);
    this.itemSelected.reset();

    setTimeout(() => {
      this.checkIfAllManufacturersAreSame();
    }, 0);
  }

  isSelected(id: string) {
    return this.formArrayItems.controls.some((x) => x.get('id')?.value === id);
  }

  isSelectedWithPO(itemId: string, purchaseOrderId: string) {
    return this.formArrayItems.controls.some(
      (x) => x.get('id')?.value === itemId && x.get('purchaseOrderReference')?.value === purchaseOrderId,
    );
  }

  deleteItemArrayForm(index: number) {
    this.formArrayItems.removeAt(index);
    setTimeout(() => {
      this.checkIfAllManufacturersAreSame();
    }, 0);
  }

  getTotalPriceAndQuantity() {
    let totalQuantitiesLbs = 0;
    let totalQuantitiesKgs = 0;
    let totalAmount1 = 0;

    this.formArrayItems.controls.forEach((item) => {
      const quantity = item.get('quantity')?.value || 0;

      // Calculate total price including tax
      const totalPriceWithTax = this.getTotalPriceWithTax(item);

      item.get('totalPrice')?.setValue(totalPriceWithTax, { emitEvent: false });

      const uom = item.get('uom')?.value;
      const uomStr = (this.uoms[uom] || '').toLowerCase().trim();
      const isKgs = uom === 0 || uomStr === 'kg' || uomStr === 'kgs' || uomStr === 'kilograms';

      if (isKgs) {
        totalQuantitiesKgs += quantity;
        totalQuantitiesLbs += quantity / 0.453592;
      } else if (uom === InvoiceDetailComponent.UOM_LB) {
        totalQuantitiesLbs += quantity;
        totalQuantitiesKgs += quantity * 0.453592;
      } else if (uom === InvoiceDetailComponent.UOM_KG) {
        totalQuantitiesKgs += quantity;
        totalQuantitiesLbs += quantity / 0.453592;
      }
      totalAmount1 += totalPriceWithTax;
    });

    this.invoiceForm.patchValue({ totalQuantitiesLbs, totalQuantitiesKgs, totalAmount1 }, { emitEvent: false });
  }

  updateTotalPrice(event: Event, index: number) {
    const quantity = (event.target as HTMLInputElement).value;
    const unitPrice = this.formArrayItems.at(index).get('unitPrice')?.value;
    const discount = this.formArrayItems.at(index).get('discount')?.value;
    const totalPrice = Number(quantity) * Number(unitPrice) - (Number(quantity) * Number(unitPrice) * Number(discount)) / 100;
    this.formArrayItems.at(index).get('totalPrice')?.setValue(totalPrice);
  }

  async save() {
    this.inputIssuedDate.markAsTouched();
    if (this.listPurchaseOrderSelected().length === 0) {
      GlobalMessageUtil.error('Error', 'Please fill all required fields');
      return;
    }

    // Get unique purchase orders that have items associated
    const purchaseOrdersWithItems = this.formArrayItems.controls.reduce((acc, item) => {
      const poRef = item.get('purchaseOrderReference')?.value;
      if (poRef) {
        acc.add(poRef);
      }
      return acc;
    }, new Set<string>()).size;

    if (this.listPurchaseOrderSelected().length != purchaseOrdersWithItems) {
      GlobalMessageUtil.warn('Warning', 'There are not item associated to one or more purchase orders selected');
      return;
    }

    const invoice: Invoice = {
      invoiceNumber: this.invoice?.invoiceNumber || undefined,
      files: this.invoice?.files || [],
      purchaseOrderReferences: this.listPurchaseOrderSelected().map((x) => x.id!) ?? [],
      purchaseOrderNumbers: this.listPurchaseOrderSelected().map((x) => x.purchaseOrderNumber!) ?? [],
      providerInvoiceNumber: this.providerInvoiceNumber.value ?? undefined,
      status: this.status.value,
      items: this.formArrayItems.value.map((item) => {
        return {
          itemInPurchaseOrderId: item.id,
          quantity: item.quantity,
          manufacturerReference: this.manufacturerAllItems.value
            ? (this.inputMaker.value?.id ?? undefined)
            : (item.manufacturerReference?.id ?? undefined),
          purchaseOrderReference: item.purchaseOrderReference,
        };
      }),
      providerReference: this.inputPurchaseOrder?.value?.providerReference,
      totalAmount: this.totalAmount,
      dueDate: this.inputDueDate.value ?? undefined,
      invoiceIssuedDate: this.inputIssuedDate.value ?? undefined,
      exchangeRateToUSD: this.inputExchangeRate.value ? this.inputExchangeRate.value : undefined,
    };

    console.log(invoice);

    if (this.isNewInvoice) {
      this._invoiceProxyService.createInvoice(invoice).subscribe({
        next: (resp) => {
          if (resp.data !== null) {
            this._resetFormAndData();
            this.invoice = resp.data;

            GlobalMessageUtil.success('Invoice created successfully', `Invoice ${resp.data.invoiceNumber} has been created successfully`);

            this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: { id: this.invoice.id },
              queryParamsHandling: 'merge',
            });
          }
        },
        error: (e) => {
          console.error(e);
          // GlobalMessageUtil.error('Error creating invoice', this.localeHelperService.getLocalizedValue(e.error.errorMessage));
        },
      });
    } else {
      this._invoiceProxyService.updateInvoice({ ...invoice, id: this.invoice.id! }).subscribe({
        next: () => {
          this.router.navigate(['/invoices']);
          GlobalMessageUtil.success('Success', `Invoice  ${this.invoice.invoiceNumber} has been updated successfully`);
        },
      });
    }
  }

  cancel() {
    this.router.navigate(['/invoices']);
    this.invoiceForm.reset();
  }

  parseDate(date: Date | string, reverse = false): Date | string {
    if (!date) return '';

    if (reverse) {
      return new Date(date);
    }

    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }

    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate.toISOString().split('T')[0];
    }

    return '';
  }

  private _resetFormAndData() {
    this.formArrayItems.clear();
  }

  viewFile(presignedUrl: string) {
    //open the file in a new tab
    window.open(presignedUrl, '_blank');
  }

  private async getFilesFromDropEntries(files: NgxFileDropEntry[]): Promise<File[]> {
    const filePromises: Promise<File>[] = [];

    for (const droppedFile of files) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;

        const filePromise = new Promise<File>((resolve, reject) => {
          fileEntry.file(resolve, reject);
        });

        filePromises.push(filePromise);
      }
    }

    return await Promise.all(filePromises);
  }

  async uploadFiles(files: NgxFileDropEntry[]): Promise<void> {
    try {
      const fileList = await this.getFilesFromDropEntries(files);

      if (!this.invoice?.id) return;

      const uploadedFiles = await lastValueFrom(this._invoiceProxyService.uploadFiles(fileList, this.invoice.id));

      if (_.isNil(this.invoice.files)) {
        this.invoice.files = [];
      }

      this.invoice.files.push(...uploadedFiles);

      GlobalMessageUtil.success('Success', 'Files uploaded successfully');
    } catch (error) {
      console.error(error);
    }
  }

  deleteFile(file: S3Object): void {
    if (!file) return;

    this._invoiceProxyService.deleteFile(this.invoice.id!, file).subscribe({
      next: (resp) => {
        if (resp && resp.success) {
          GlobalMessageUtil.success(
            this.translateService.instant('Success'),
            this.translateService.instant('InvoiceDetailPage_Messages.FilesDeletedSuccessfully'),
          );
          this.invoice.files = this.invoice.files!.filter((x) => x.presignedUrl !== file.presignedUrl);
        }
      },
      error: (e) => {
        console.error(e);
      },
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this._invoiceProxyService.uploadFiles(Array.from(input.files!), this.invoice.id!).subscribe({
      next: (resp) => {
        if (_.isNil(this.invoice.files)) this.invoice.files = [];
        this.invoice.files.push(...resp);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Files uploaded successfully',
          life: 2500,
          key: 'main',
        });
      },
      error: (e) => {
        console.error(e);
      },
    });
  }

  async submitFiles(): Promise<S3Object[]> {
    if (this.currentFiles().length <= 0) return [];
    const resp = await lastValueFrom(this._invoiceProxyService.uploadFiles(this.currentFiles(), this.invoice.id!));
    if (!resp) return [];
    return resp;
  }

  removeFile(index: number) {
    this.currentFiles().filter((_, i) => i !== index);
  }

  getFileName(nameWithPrefix: string): string {
    const lastSlashIndex = nameWithPrefix.lastIndexOf('/');
    if (lastSlashIndex === -1) {
      return nameWithPrefix; // No slash found, return the original string
    }
    return nameWithPrefix.substring(lastSlashIndex + 1); // Return the substring after the last slash
  }

  private processItemsWithoutManufacturers(items: InvoiceItem[]) {
    items.forEach((item) => {
      const itemData = this.purchase.items.find((x) => x.id === item.itemInPurchaseOrderId);
      if (itemData) {
        this.listItemsOriginal.push(itemData);
        this._pushItemArrayForm(this.setDataIntoItemForm(itemData, item.quantity));
      }
    });
  }

  private checkIfAllManufacturersAreSame(): void {
    if (this.isManualManufacturerControl) {
      return;
    }

    const items = this.formArrayItems.controls;

    if (items.length === 0) {
      this.manufacturerAllItems.setValue(false);
      return;
    }

    const firstManufacturer = items[0].get('manufacturerReference')?.value;

    if (!firstManufacturer || !firstManufacturer.id) {
      this.manufacturerAllItems.setValue(false);
      return;
    }

    const allSame = items.every((item) => {
      const manufacturer = item.get('manufacturerReference')?.value;
      return manufacturer && manufacturer.id === firstManufacturer.id;
    });

    if (allSame && !this.manufacturerAllItems.value) {
      this.manufacturerAllItems.setValue(true, { emitEvent: false });
      this.inputMaker.setValue(firstManufacturer);
      this.inputMaker.enable();
      items.forEach((item) => {
        item.get('manufacturerReference')?.disable();
      });
    }
  }

  private resetManualControl(): void {
    this.isManualManufacturerControl = false;
  }

  public validatePOByTypeMatchSelected() {
    const poType = this.listPurchaseOrderSelected()[0].type;
    return this.listPurchaseOrderSelected().some((po) => po.type === poType);
  }

  /**
   * Gets the currency code for the selected purchase orders
   */
  getCurrentCurrency(): number {
    const selectedPOs = this.listPurchaseOrderSelected();
    if (selectedPOs.length === 0) return 0; // USD
    return selectedPOs[0].currency;
  }

  /**
   * Gets the currency symbol/code as string for display
   */
  getCurrencySymbol(): string {
    const currency = this.getCurrentCurrency();
    const currencyEnum = this.enumService.getEnum('Currency', 'financeEnums')?.values;
    return currencyEnum?.[currency] || 'USD';
  }

  /**
   * Calculates unit price including tax for a given item
   */
  getUnitPriceWithTax(formGroup: FormGroup): number {
    const unitPrice = formGroup.get('unitPrice')?.value || 0;

    // Get the item from the PO to check if it's tax exempt
    const itemId = formGroup.get('id')?.value;
    const poRef = formGroup.get('purchaseOrderReference')?.value;

    if (!poRef) return unitPrice;

    const selectedPO = this.listPurchaseOrderSelected().find((po) => po.id === poRef);
    if (!selectedPO) return unitPrice;

    const poItem = selectedPO.items.find((item) => item.id === itemId);
    if (!poItem) return unitPrice;

    // Check tax rate - if taxRate is 0 or undefined, item is tax exempt
    const taxRate = poItem.taxRate || 0;
    return unitPrice + unitPrice * taxRate;
  }

  /**
   * Calculates total price including tax for a given item
   */
  getTotalPriceWithTax(formGroup: FormGroup): number {
    const quantity = formGroup.get('quantity')?.value || 0;
    const unitPriceWithTax = this.getUnitPriceWithTax(formGroup);
    const discount = formGroup.get('discount')?.value || 0;

    return quantity * unitPriceWithTax - discount;
  }

  /**
   * Checks if an item has a tax rate > 0
   */
  itemHasTax(formGroup: FormGroup): boolean {
    const itemId = formGroup.get('id')?.value;
    const poRef = formGroup.get('purchaseOrderReference')?.value;

    if (!poRef) return false;

    const selectedPO = this.listPurchaseOrderSelected().find((po) => po.id === poRef);
    if (!selectedPO) return false;

    const poItem = selectedPO.items.find((item) => item.id === itemId);
    if (!poItem) return false;

    return (poItem.taxRate || 0) > 0;
  }

  /**
   * Gets the status name from the enum
   */
  getInvoiceStatusName(status: number): string {
    return this.enumValues[status] ?? `Status ${status}`;
  }

  /**
   * Checks if the invoice can be cancelled based on two conditions:
   * 1. Invoice must not be in a shipment
   * 2. Invoice must not have lot items
   */
  async checkCanCancelInvoice(): Promise<{ allowed: boolean; reason?: string }> {
    if (!this.invoice?.id) {
      return { allowed: false, reason: 'Invoice not found' };
    }

    try {
      this.isLoading++;

      // Check if invoice is in a shipment
      const shipmentCheck = await lastValueFrom(this._shipmentProxyService.getShipments().pipe(take(1)));

      const shipmentsWithInvoice = (shipmentCheck.data ?? []).filter((shipment) => shipment.invoiceReferences?.includes(this.invoice!.id!));

      if (shipmentsWithInvoice.length > 0) {
        this.isLoading--;
        return {
          allowed: false,
          reason: this.translateService.instant('Invoice_Cannot_Cancel_Has_Shipment', {
            default: 'This invoice cannot be cancelled because it is associated with one or more shipments.',
          }),
        };
      }

      // Check if invoice has lot items
      const lotItemsCheck = await lastValueFrom(
        this.inventoryLotProxyService.getLotsByInvoiceId(this.invoice!.id!, true, false).pipe(take(1)),
      );

      const hasLotItems = (lotItemsCheck.data ?? []).length > 0;

      if (hasLotItems) {
        this.isLoading--;
        return {
          allowed: false,
          reason: this.translateService.instant('Invoice_Cannot_Cancel_Has_LotItems', {
            default: 'This invoice cannot be cancelled because it has associated lot items.',
          }),
        };
      }

      this.isLoading--;
      return { allowed: true };
    } catch (error) {
      this.isLoading--;
      console.error('Error checking if invoice can be cancelled:', error);
      return { allowed: false, reason: 'Error checking invoice cancellation eligibility' };
    }
  }

  /**
   * Shows the status change confirmation dialog
   */
  private showStatusChangeConfirmation(currentStatusName: string, newStatusName: string, isCancelling: boolean, newStatus: number): void {
    const header = isCancelling
      ? this.translateService.instant('Invoice_Status_Cancel_Title', { default: 'Cancel Invoice' })
      : this.translateService.instant('Invoice_Status_Change_Title', { default: 'Confirm Status Change' });

    const message = isCancelling
      ? this.translateService.instant('Invoice_Status_Cancel_Message', {
          default:
            'Warning: This action is irreversible. Once cancelled, this invoice cannot be restored. Are you sure you want to cancel this invoice?',
        })
      : this.translateService.instant('Invoice_Status_Change_Message', {
          current: currentStatusName,
          new: newStatusName,
          default: `Are you sure you want to change the invoice status from "${currentStatusName}" to "${newStatusName}"?`,
        });

    this.confirmationService.confirm({
      key: 'invoice-status-confirm',
      header: header,
      message: message,
      icon: isCancelling ? 'pi pi-exclamation-circle' : 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('Yes'),
      rejectLabel: this.translateService.instant('No'),
      acceptButtonStyleClass: isCancelling ? 'p-button-danger' : '',
      accept: () => {
        // Update the invoice status
        this.isLoading++;
        const updatedInvoice = { ...this.invoice, status: newStatus };

        this._invoiceProxyService.updateInvoice(updatedInvoice).subscribe({
          next: (result) => {
            this.isLoading--;
            if (result.success) {
              this.invoice.status = newStatus;
              GlobalMessageUtil.success('Invoice Status', 'Invoice status updated successfully');
            }
          },
          error: () => {
            this.isLoading--;
            this.status.setValue(this.invoice!.status, { emitEvent: false });
          },
        });
      },
      reject: () => {
        // Revert to previous status if user cancels
        this.status.setValue(this.invoice!.status, { emitEvent: false });
      },
    });
  }
}
